import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

export default function GalleryDetail() {
  const { id } = useParams();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    setLoading(true);
    api.get(`/albums/${id}`)
      .then(r => setAlbum(r.data))
      .catch(() => setAlbum(null))
      .finally(() => setLoading(false));
  }, [id]);

  const close = useCallback(() => setLightboxIdx(-1), []);
  const prev = useCallback(() => setLightboxIdx(i => (album?.images?.length ? (i - 1 + album.images.length) % album.images.length : i)), [album]);
  const next = useCallback(() => setLightboxIdx(i => (album?.images?.length ? (i + 1) % album.images.length : i)), [album]);

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIdx < 0) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, close, prev, next]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-aubergine/20 border-t-aubergine rounded-full animate-spin" />
      </div>
    );
  }

  if (!album) {
    return (
      <div className="section-pad container-luxe text-center">
        <p className="font-display text-3xl text-aubergine">Album not found</p>
        <Link to="/gallery" className="mt-6 inline-flex items-center gap-2 text-plum hover:text-aubergine">
          <ArrowLeft className="w-4 h-4" /> Back to gallery
        </Link>
      </div>
    );
  }

  const dateStr = album.date ? new Date(album.date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div data-testid="gallery-detail">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={album.cover_image} alt={album.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-aubergine/70 via-aubergine/60 to-aubergine/85" />
        </div>
        <div className="relative container-luxe py-16 md:py-24 text-ivory">
          <Link to="/gallery" data-testid="gd-back" className="inline-flex items-center gap-2 text-ivory/80 hover:text-ivory text-[12px] tracking-[0.2em] uppercase">
            <ArrowLeft className="w-4 h-4" /> All albums
          </Link>
          <span className="mt-6 inline-block px-3 py-1 rounded-full bg-ivory/12 border border-ivory/20 text-[10px] tracking-[0.28em] uppercase text-champagne">
            {album.category}
          </span>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] mt-4 max-w-4xl">{album.title}</h1>
          <div className="mt-6 flex flex-wrap gap-6 text-[13px] text-ivory/90">
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-champagne" />{album.location}{album.city ? `, ${album.city}` : ""}</span>
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-champagne" />{dateStr}</span>
          </div>
          {album.description && (
            <p className="mt-6 max-w-3xl text-ivory/85 text-base md:text-lg leading-relaxed">{album.description}</p>
          )}
        </div>
      </section>

      {/* MASONRY */}
      <section className="container-luxe py-14 md:py-16">
        {album.images?.length > 0 ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
            {album.images.map((img, i) => (
              <button
                key={i}
                data-testid={`gd-img-${i}`}
                onClick={() => setLightboxIdx(i)}
                className="w-full block break-inside-avoid rounded-2xl overflow-hidden border border-aubergine/[0.08] bg-lavender/40 group focus:outline-none focus:ring-2 focus:ring-aubergine/40"
              >
                <img
                  src={img.url}
                  alt={img.caption || album.title}
                  loading="lazy"
                  className="w-full block group-hover:scale-[1.02] transition-transform duration-500"
                />
                {img.caption && (
                  <div className="px-4 py-3 text-[12px] text-plumSoft text-left">
                    {img.caption}
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-center text-plumSoft py-16">Photos coming soon.</p>
        )}
      </section>

      {/* LIGHTBOX */}
      {lightboxIdx >= 0 && album.images?.[lightboxIdx] && (
        <div className="fixed inset-0 z-[100] bg-aubergine/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="gd-lightbox">
          <button
            onClick={close}
            aria-label="Close"
            data-testid="gd-lb-close"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 text-ivory flex items-center justify-center transition"
          >
            <X className="w-5 h-5" />
          </button>
          {album.images.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous"
                data-testid="gd-lb-prev"
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 text-ivory flex items-center justify-center transition"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="Next"
                data-testid="gd-lb-next"
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 text-ivory flex items-center justify-center transition"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <div className="max-w-[92vw] max-h-[85vh] flex flex-col items-center gap-4">
            <img src={album.images[lightboxIdx].url} alt={album.images[lightboxIdx].caption || ""} className="max-w-full max-h-[75vh] rounded-xl object-contain" />
            <div className="text-ivory text-center max-w-2xl">
              {album.images[lightboxIdx].caption && (
                <p className="text-sm md:text-base leading-relaxed">{album.images[lightboxIdx].caption}</p>
              )}
              <p className="mt-1 text-[10px] tracking-[0.28em] uppercase text-ivory/60">
                {lightboxIdx + 1} / {album.images.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
